/* eslint-disable multiline-ternary */
import { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import DriveThruLobbyToggle from '@/components/buttons/DriveThruLobbyToggle';
import Datepicker from '@/components/time/Datepicker';
import DropdownLocationMultiple from '@/components/dropdowns/DropdownLocationMultiple';
import { Toaster, toast } from 'sonner';
import { type ReportConfig } from '@/types/ReportConfig';
import DropdownFull from '@/components/dropdowns/DropdownFull';
import DropdownFilter from '@/components/dropdowns/DropdownFilter';
import {
  getAllReportConfigs,
  getManualReport,
  saveNewReport,
  updateReport,
  deleteReportConfig,
  getAllGroups,
  getGroupLocationsByName,
} from '@/utils/api';
import { useAuth } from '@/hooks/useAuth';
import GroupsButton from '@/components/buttons/GroupsButton';
import ModalBasic from '@/components/modals/ModalBasic';
import GroupModal from '@/components/modals/GroupModal';
import { type GroupResponse } from '@/types/GroupResponse';
import { type FranchiseRegion } from '@/types/FranchiseConfiguration';
import { type DayPart } from '@/types/DayPart';
import { Roles } from '@/types/Roles';

export default function ReportConfigs(): JSX.Element {
  const { user } = useAuth();
  const {
    selectedFranchise,
    selectedLocations,
    selectedDates,
    handleToggle,
    toggled,
    handleSelectedDates,
    profile,
    setSelectedLocations,
    handleSelectGroups,
    cachedGroups,
    setCachedGroups,
  } = useData();

  const [selectedReportConfigId, setSelectedReportConfigId] =
    useState<string>('');

  const [fetchGroupData, setFetchGroupData] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disabledSpan, setDisabledSpan] = useState<boolean>(false);
  const [groupOptions, setGroupOptions] = useState<GroupResponse[]>([]);

  const [reportConfigOptions, setReportConfigOptions] = useState<
    ReportConfig[]
  >([]);
  const [selectedReportConfig, setSelectedReportConfig] = useState({
    displayName: '-- Create New Report --',
    reportId: '',
  });

  const reportTypes = [
    { label: 'Trends & Region/Daypart', value: 'ANALYTICSPAGE' },
    { label: 'Pivot', value: 'PIVOTCOMP' },
    { label: 'Customer Journey', value: 'CUSTOMERJOURNEY' },
    { label: 'Ranks Report', value: 'OLDREPORT' },
  ];
  const [selectedReportType, setSelectedReportType] = useState({
    label: 'Select Report Type',
    value: '',
  });

  const [selectedReportName, setSelectedReportName] = useState<string>('');
  
  const pivotSplitBy = [
    { label: 'Day of the week', value: 'dayOfWeek' },
    { label: 'DayPart', value: 'daypart' },
    { label: '60 Minute Blocks', value: '60min' },
    { label: 'Stations', value: 'region' },
    { label: 'Date', value: 'date' },
  ];
  const [selectedSplitBy, setSelectedSplitBy] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const pivotMeasures = [
    { label: 'Average Customer Time', value: 'avg_time' },
    { label: 'Volume Total', value: 'customer_count' },
    { label: 'Revenue Average', value: 'revenue' },
  ];
  const [selectedMeasures, setSelectedMeasures] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const TacoBellTiles = [
    { label: 'Total Journey Time Count', value: 'Total Journey Time Count' },
    { label: 'Total Journey Time Percentage', value: 'Total Journey Time Percentage' },
    { label: 'Drive Offs', value: 'Drive Offs'},
    { label: 'Max Car Stack', value: 'Max Car Stack' },
    { label: 'Max Total Journey Time', value: 'Max Total Journey Time' },
    { label: 'Max O.T.D.', value: 'Max O.T.D.' },

  ]

  const CFSTiles = [
    { label: 'Avg. Pull Forward TIme', value: 'AVG. PULL FORWARD TIME' },
    { label: 'Avg. Total Service Time', value: 'AVG. TOTAL SERVICE TIME' },
    { label: 'DriveThru Metric Comp', value: 'DRIVE THRU METRICS'},
    { label: 'Curdside Metrics Comp', value: 'CURDSIDE' },
  ]
  const franchiseId = selectedFranchise?.id;
  const availableTiles = (franchiseId === '64') ? TacoBellTiles : CFSTiles;
  const [selectedTiles, setSelectedTiles] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const timeSpans = [
    { label: 'Last Day', value: 'LAST1DAY' },
    { label: 'Last 7 Days', value: 'LAST7DAYS' },
    { label: 'Last 30 Days', value: 'LAST30DAYS' },
    { label: 'Previous Quarter', value: 'PREVIOUSQUARTER' },
    { label: 'Current Quarter', value: 'QUARTERTODATE' },
    { label: 'Previous Month', value: 'PREVIOUSMONTH' },
    { label: 'Current Month', value: 'MONTHTODATE' },
    { label: 'Current Year', value: 'YEARTODATE' },
    { label: 'Date Range', value: 'SPECIFICDATES' },
  ];
  const [selectedTimeSpans, setSelectedTimeSpans] = useState({
    label: 'Date Range',
    value: '',
  });

  // FILTERING
  // Inside your ReportConfigs component
  const filterCategories = [
    { label: 'Stations', value: 'region' },
    { label: 'DayPart', value: 'daypart' },
    { label: 'Day of the week', value: 'dayOfWeek' },
    { label: 'Date', value: 'date' },
  ];

  const [selectedFilterCategories, setSelectedFilterCategories] = useState<
    Array<{ value: string; label: string }>
  >([]);

  // State for the actual selected filter items (e.g., { 'region': ['station_a_key', 'station_b_key'], 'dayOfWeek': ['Monday'] })
  const [selectedFilterItems, setSelectedFilterItems] = useState<
    Record<string, string[]> // Map: 'filterCategoryValue' -> array of selected 'itemId' strings
  >({});

  // State to cache the options available for each filter category (e.g., all station names, all day part names)
  // This will hold options like { 'region': [{ id: 'station_a_key', value: 'Station A' }], 'dayPart': [...] }
  const [availableFilterOptions, setAvailableFilterOptions] = useState<
    Record<string, Array<{ id: string; value: string }>>
  >({});

  const fetchOptions = async (category: string) => {
      try {
        if (selectedFranchise) {
          if (category === 'region') {
            if (toggled === 'DT') {
              const options =
                selectedFranchise.configuration.configuration.driveThruConfiguration.regions.map(
                  (item: FranchiseRegion) => ({
                    id: item.key,
                    value: item.displayName,
                  })
                );
              setAvailableFilterOptions((prev) => ({
                ...prev,
                region: naturalSortOptions(options),
              }));
            } else {
              const options =
                selectedFranchise.configuration.configuration.lobbyConfiguration.regions.map(
                  (item: FranchiseRegion) => ({
                    id: item.key,
                    value: item.displayName,
                  })
                );
              setAvailableFilterOptions((prev) => ({
                ...prev,
                region: naturalSortOptions(options),
              }));
            }

            setSelectedFilterItems((prevItems) => {
              const newItems = { ...prevItems };
              delete newItems.region; // Remove the items associated with this category
              return newItems;
            });
          }
          if (category === 'daypart') {
            const options =
              selectedFranchise.configuration.configuration.driveThruConfiguration.dayParts.map(
                (item: DayPart) => ({
                  id: item.name,
                  value: item.name,
                })
              );
            setAvailableFilterOptions((prev) => ({
              ...prev,
              daypart: naturalSortOptions(options),
            }));
            setSelectedFilterItems((prevItems) => {
              const newItems = { ...prevItems };
              delete newItems.daypart; // Remove the items associated with this category
              return newItems;
            });
          }
          if (category === 'dayOfWeek') {
            const options = [
              {
                id: 'Monday',
                value: 'Monday',
              },
              {
                id: 'Tuesday',
                value: 'Tuesday',
              },
              {
                id: 'Wednesday',
                value: 'Wednesday',
              },
              {
                id: 'Thursday',
                value: 'Thursday',
              },
              {
                id: 'Friday',
                value: 'Friday',
              },
              {
                id: 'Saturday',
                value: 'Saturday',
              },
              {
                id: 'Sunday',
                value: 'Sunday',
              }
            ];
            setAvailableFilterOptions((prev) => ({
              ...prev,
              dayOfWeek: options,
            }));
            setSelectedFilterItems((prevItems) => {
              const newItems = { ...prevItems };
              delete newItems.dayOfWeek; // Remove the items associated with this category
              return newItems;
            });
          }
        }
      } catch (error) {
        console.error(`Failed to fetch options for ${category}:`, error);
      }
    };

  useEffect(() => {
    const franchiseId = selectedFranchise?.id;
    if (!franchiseId) return;
    // Helper function to fetch specific options based on category

    // Trigger fetching options for newly selected filter categories
    selectedFilterCategories.forEach((category) => {
      // Only fetch if not already cached
      void fetchOptions(category.value);
    });

    // Also clear existing filter items if a category is deselected
    // This is handled in handleSelectedFilterCategoriesChange now.
  }, [
    selectedFilterCategories,
    selectedFranchise,
    toggled,
  ]); // Add availableFilterOptions to deps

  const handleSelectedFilterCategoriesChange = async (id: string) => {
    const matchedCategory = filterCategories.find((cat) => cat.value === id);

    if (matchedCategory) {
      setSelectedFilterCategories((prevSelected) => {
        const isSelected = prevSelected.some((sc) => sc.value === id);
        const newSelectedCategories = isSelected
          ? prevSelected.filter((sc) => sc.value !== id) // Deselect
          : [...prevSelected, matchedCategory]; // Select

        // --- IMPORTANT: Clear items for deselected categories ---
        if (isSelected) {
          // If a category is being deselected, clear its chosen items
          setSelectedFilterItems((prevItems) => {
            const newItems = { ...prevItems };
            delete newItems[id]; // Remove the items associated with this category
            return newItems;
          });
        } else {
          // If a new category is being selected, initialize its item array as empty
          setSelectedFilterItems((prevItems) => ({
            ...prevItems,
            [id]: prevItems[id] || [], // Initialize to empty array if not already present
          }));
        }

        return newSelectedCategories;
      });
    }
  };

  // Handles selection/deselection of specific FILTER ITEMS (e.g., 'Station A', 'Monday')
  const handleSelectedFilterItemsChange = (
    category: string,
    itemId: string
  ) => {
    setSelectedFilterItems((prevItems) => {
      const currentItems = prevItems[category] || []; // Get current items for this category
      const isSelected = currentItems.includes(itemId);
      const newItems = isSelected
        ? currentItems.filter((item) => item !== itemId) // Deselect item
        : [...currentItems, itemId]; // Select item
      return {
        ...prevItems,
        [category]: newItems, // Update items for this specific category
      };
    });
  };

  const fetchReportConfigs = async (): Promise<void> => {
    if (selectedFranchise != null) {
      const noneConfig: ReportConfig[] = [
        {
          displayName: '-- Create New Report --',
          reportId: '',
          locations: [],
          detectionType: 'DT',
          timeSpan: '',
          reportType: '',
          startDate: '',
          endDate: '',
          measures: [],
          splitBys: [],
          createUser: '',
        },
      ];
      const newReportConfigs = await getAllReportConfigs(selectedFranchise?.id);
      setReportConfigOptions([...noneConfig, ...newReportConfigs]);
    }
  };

  useEffect(() => {
    setSelectedReportConfigId('');
    handleSelectedReportConfigChange('');

    const franchiseId = selectedFranchise?.id || '';
    if (!franchiseId || !selectedFranchise) {
      return;
    }

    void fetchReportConfigs();
    getAllGroups(franchiseId).then((res) => {
      setGroupOptions(res);
    });
    // clear all the selected values when the franchise changes
  }, [selectedFranchise]);

  useEffect(() => {
    const franchiseId = selectedFranchise?.id || '';
    if (cachedGroups.length === 0 || !franchiseId) {
      return;
    }
    // Fetch group locations
    Promise.all(
      cachedGroups.map(
        async (group) => await getGroupLocationsByName(group, franchiseId)
      )
    )
      .then((responses) => {
        // Flatten all groupLocations arrays (which are arrays of location IDs)
        const allLocationIds = responses.flat();
        // Remove duplicates
        const uniqueLocationIds = Array.from(new Set(allLocationIds));
        // Map IDs to location objects from selectedLocations (or another source if needed)
        handleSelectGroups(cachedGroups, uniqueLocationIds, '');
      })
      .catch((error) => {
        console.error(error);
      });
  }, [cachedGroups]);

  useEffect(() => {
    if (selectedReportConfigId !== '') {
      void handleSelectedReportConfigChange(selectedReportConfigId);
    }
  }, [reportConfigOptions]);

  const naturalSortOptions = (
    options: Array<{ id: string; value: string }>
  ) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return [...options].sort((a, b) => collator.compare(a.value, b.value));
  };

  async function handleSendReportSelf(): Promise<void> {
    const franchiseId = selectedFranchise?.id ?? '';
    if (
      franchiseId === '' ||
      selectedLocations.length === 0 ||
      selectedDates.length === 0
    ) {
      console.error('Missing required data to generate the report.');
      return;
    }

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDate = formatDate(selectedDates[0]);
    const endDate = formatDate(selectedDates[1] ?? selectedDates[0]);

    setIsLoading(true);

    try {
      let groupIds: string[] = [];
      let locationIds: string[] = selectedLocations.map((location) =>
        location.id.toString()
      );
      if (fetchGroupData) {
        groupIds = cachedGroups.map((group) => group.groupId);
        locationIds = [];
      }
      await getManualReport(
        franchiseId,
        locationIds,
        startDate,
        endDate,
        toggled,
        selectedReportType.value,
        selectedTimeSpans.value,
        selectedSplitBy.map((splitBy) => splitBy.value),
        selectedMeasures.map((measure) => measure.value.toString()),
        groupIds,
        selectedTiles.map((tile) => tile.value.toString()),
        selectedFilterItems
      ).then(() => toast('Successfully scheduled an email to self.'));
    } catch (E) {
      toast.error('Failed to get manual report');
    }
    finally {
      setIsLoading(false);
    }
  }

  async function handleSaveReport(): Promise<void> {
    const franchiseId = selectedFranchise?.id ?? '';
    if (
      franchiseId === '' ||
      selectedLocations.length === 0 ||
      selectedDates.length === 0
    ) {
      console.error('Missing required data to generate the report.');
      return;
    }

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDate = formatDate(selectedDates[0]);
    const endDate = formatDate(selectedDates[1] ?? selectedDates[0]);
    let detectionType = toggled;
    let groupIds: string[] = [];
    let locationIds: string[] = selectedLocations.map((location) =>
      location.id.toString()
    );
    if (fetchGroupData) {
      groupIds = cachedGroups.map((group) => group.groupId);
      locationIds = [];
    }

    // set filters

    if (selectedReportConfig.reportId !== '') {
      await updateReport(
        selectedReportConfig.reportId,
        franchiseId,
        selectedReportName,
        locationIds,
        detectionType,
        selectedTimeSpans.value,
        selectedReportType.value,
        startDate,
        endDate,
        selectedMeasures.map((measure) => measure.value.toString()),
        selectedSplitBy.map((splitBy) => splitBy.value.toString()),
        groupIds,
        selectedTiles.map((tile) => tile.value.toString()),
        selectedFilterItems
      )
        .then(() => toast('Successfully updated report config.'))
        .then(async () => {
          await fetchReportConfigs();
        });
    } else {
      await saveNewReport(
        franchiseId,
        selectedReportName,
        locationIds,
        detectionType,
        selectedTimeSpans.value,
        selectedReportType.value,
        startDate,
        endDate,
        selectedMeasures.map((measure) => measure.value.toString()),
        selectedSplitBy.map((splitBy) => splitBy.value.toString()),
        groupIds,
        selectedTiles.map((tile) => tile.value.toString()),
        selectedFilterItems
      )
        .then((newReport) => {
          if (
            newReport !== undefined &&
            newReport !== null &&
            typeof newReport !== 'boolean' &&
            newReport.reportId !== ''
          ) {
            setSelectedReportConfigId(newReport.reportId);
          }
        })
        .then(async () => {
          await fetchReportConfigs();
        });
    }
  }
  async function handleDeleteReport(): Promise<void> {
    const franchiseId = selectedFranchise?.id ?? '';
    if (
      franchiseId === '' ||
      selectedLocations.length === 0 ||
      selectedDates.length === 0 ||
      selectedReportConfig.reportId === '' ||
      selectedReportConfig.reportId === undefined ||
      selectedReportConfig.reportId === null
    ) {
      console.error('Missing required data to delete the report config.');
      return;
    }

    // TODO validate that the report config is not in use by a schedule before deleting

    await deleteReportConfig(selectedReportConfig.reportId, franchiseId)
      .then(() => toast('Successfully deleted report config.'))
      .then(async () => {
        await fetchReportConfigs();
        handleSelectedReportConfigChange('');
      });
  }

  function handleDateChange(dates: Date[]): void {
    handleSelectedDates(dates);
  }

  const handleSelectedReportTypeChange = (id: string) => {
    const matched = reportTypes.find((rt) => rt.value === id);
    if (matched != null) {
      setSelectedReportType(matched);
      if (matched.value === 'CUSTOMERJOURNEY')
      {
        setDisabledSpan(true);
        handleSelectedTimeSpanChange('LAST1DAY');
      }
      else {
        setDisabledSpan(false);
        // handleSelectedTimeSpanChange('LAST1DAY');
        setSelectedTimeSpans({
          label: 'Date Range',
          value: '',
        });
      }
    }
  };

  const handleSelectedTimeSpanChange = (id: string) => {
    const matched = timeSpans.find((rt) => rt.value === id);
    if (matched != null) {
      setSelectedTimeSpans(matched);
    }
  };

  const handleSelectedMeasuresChange = (id: string) => {
    const matched = pivotMeasures.find((rt) => rt.value === id);
    if (matched != null) {
      setSelectedMeasures(
        selectedMeasures.some((sm: { value: string }) => sm.value === id)
          ? selectedMeasures.filter((v) => v.value !== matched.value)
          : [...selectedMeasures, matched]
      );
    }

    const isDriveoffsOrRevenue =  id === 'driveoffs';
    if (isDriveoffsOrRevenue) {
      const isRegionConflict = selectedSplitBy.some(
        (sm: { value: string }) => sm.value === 'region'
      );
      if (isRegionConflict) {
        setSelectedSplitBy(selectedSplitBy.filter((v) => v.value !== 'region'));
        toast('Removed region from selector', {
          description: 'Region is not compatible with driveoffs/revenue',
          action: {
            label: 'Ok',
            onClick: () => {
              console.log('Ok');
            },
          },
        });
      }
    }
  };

  const handleRefreshGroups = () => {
    if (selectedFranchise) {
      getAllGroups(selectedFranchise.id).then((res) => {
        setGroupOptions(res);
      });
    }
  };

  const handleSelectedSplitByChange = (id: string) => {
    const matched = pivotSplitBy.find((rt) => rt.value === id);

    if (matched != null) {
      setSelectedSplitBy(
        selectedSplitBy.some((ss: { value: string }) => ss.value === id)
          ? selectedSplitBy.filter((v) => v.value !== matched.value)
          : [...selectedSplitBy, matched]
      );

      const isRegion = id === 'region';
      if (isRegion) {
        const isRegionConflict = selectedMeasures.some(
          (sm: { value: string }) =>
           sm.value === 'driveoffs'
        );
        if (isRegionConflict) {
          setSelectedMeasures(
            selectedMeasures.filter(
              (v) => v.value !== 'driveoffs'
            )
          );
          toast('Removed revenue from selector', {
            description: 'Revenue is not compatible with regions',
            action: {
              label: 'Ok',
              onClick: () => {
                console.log('Ok');
              },
            },
          });
        }
      }
    }
  };


  const handleSelectedTilesChange = (id: string) => {
    const matched = availableTiles.find((rt) => rt.value === id);

    if (matched != null) {
      setSelectedTiles(
        selectedTiles.some((ss: { value: string }) => ss.value === id)
          ? selectedTiles.filter((v) => v.value !== matched.value)
          : [...selectedTiles, matched]
      );
    }
  };

  const handleSelectedReportConfigChange = async (id: string) => {
    const matched = reportConfigOptions.find((rt) => rt.reportId === id);
    // change all the dropdowns based off of the selectedReportConfig
    setSelectedReportConfigId(id);
    // setSelectedFilterCategories([]);
    selectedFilterCategories.forEach((category) => {
      handleSelectedFilterCategoriesChange(category.value);
    }
    );
    setSelectedFilterItems((prevItems) => {
              const newItems = { ...prevItems };
              newItems.region = [];
              newItems.dayOfWeek = [];
              newItems.daypart = [];
              return newItems;
      });

    if (matched !== undefined) {
      // report Name
      setSelectedReportName(
        matched.displayName !== '-- Create New Report --'
          ? matched.displayName
          : ''
      );

      // reportType
      const configReportType = reportTypes.find(
        (rt) => rt.value === matched.reportType
      );
      if (configReportType !== undefined) {
        setSelectedReportType(configReportType);
      } else {
        setSelectedReportType({ label: 'Select Report Type', value: '' });
      }

      // ReportSpan
      const configTimeSPan = timeSpans.find(
        (rt) => rt.value === matched.timeSpan
      );
      if (configTimeSPan !== undefined) {
        setSelectedTimeSpans(configTimeSPan);
      } else {
        setSelectedTimeSpans({ label: 'Select Time Span', value: '' });
      }

      // Handle specific dates
      if (matched.timeSpan === 'SPECIFICDATES') {
        const newDates = [
          new Date(matched.startDate),
          new Date(matched.endDate),
        ];
        handleDateChange(newDates);
      }

      // match filters
      if (matched.filters) {
       const newSelectedFilterItems: Record<string, string[]> = {};
       const categoriesWithDataInConfig: string[] = [];

        if (matched.filters.dayOfWeek && matched.filters.dayOfWeek.length !== 0) {
          handleSelectedFilterCategoriesChange('dayOfWeek');
          categoriesWithDataInConfig.push('dayOfWeek'); // Mark this category needs options

          newSelectedFilterItems.dayOfWeek = matched.filters.dayOfWeek;
        }
        if (matched.filters.region && matched.filters.region.length !== 0) {
          handleSelectedFilterCategoriesChange('region');
          newSelectedFilterItems.region = matched.filters.region;
          categoriesWithDataInConfig.push('region'); // Mark this category needs options
        }
        if (matched.filters.daypart && matched.filters.daypart.length !== 0) {
          handleSelectedFilterCategoriesChange('daypart');
          newSelectedFilterItems.daypart = matched.filters.daypart;
          categoriesWithDataInConfig.push('daypart'); // Mark this category needs options
        }

        const fetchOptionPromises = categoriesWithDataInConfig.map(async categoryValue => {
            // Check if options for this category are already available in `availableFilterOptions`
            if (!availableFilterOptions[categoryValue]) {
                // If not, explicitly call the fetchOptions helper for this category.
                // The fetchOptions function itself should update availableFilterOptions state.
                await fetchOptions(categoryValue);
            }
        });

        // 3. Await all necessary filter options to be fetched/available
        // This is the crucial await that ensures options are present before setting selections.
        await Promise.all(fetchOptionPromises);

        setSelectedFilterItems(newSelectedFilterItems);
      }

      // Groups OR Locations
      if (matched.groups && matched.groups.length !== 0) {
        const listGroups = groupOptions.filter((group) =>
          matched.groups.includes(group.groupId)
        );

        if (listGroups !== undefined) {
          setCachedGroups(listGroups);
          setFetchGroupData(true);
        }
      } else {
        const listLocs = profile?.locations.filter(
          (loc) =>
            matched.locations.includes(loc.id.toString()) &&
            loc.franchiseId === selectedFranchise?.id
        );
        if (listLocs !== undefined) {
          setSelectedLocations(listLocs);
        }
        setFetchGroupData(false);
      }

      // Detection Type
      const toggleType = matched.detectionType;
      handleToggle(toggleType);

      // Handle measures
      const configMeasures = pivotMeasures.filter((measure) =>
        matched.measures.includes(measure.value)
      );
      setSelectedMeasures(configMeasures);

      // Handle splitbys
      const configSplitBys = pivotSplitBy.filter((splitBy) =>
        matched.splitBys.includes(splitBy.value)
      );
      setSelectedSplitBy(configSplitBys);
      setSelectedReportConfig(matched);
    }
  };

  return (
    <>
      
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 space-y-6 border border-slate-200 dark:border-slate-700">
          <Toaster />
          <div data-cy="analytics" className="space-y-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">
              Report Configuration
            </h1>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Existing Reports
                  </label>
                  <DropdownFull
                    options={reportConfigOptions.map(
                      (reportConfig: ReportConfig) => ({
                        id: reportConfig.reportId,
                        value: reportConfig.displayName,
                      })
                    )}
                    selected={{
                      id: selectedReportConfig.reportId,
                      value: selectedReportConfig.displayName,
                    }}
                    handleChange={handleSelectedReportConfigChange}
                    dataCy="dropdown-existing-config"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Report Title
                  </label>
                  <input
                    type="text"
                    value={selectedReportName}
                    className="form-input bg-white dark:bg-slate-800 w-full"
                    onChange={(e) => {
                      setSelectedReportName(e.target.value);
                    }}
                    data-cy="config-name-input"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Report Type*
                  </label>
                  <DropdownFull
                    options={reportTypes.map((reportType) => ({
                      id: reportType.value,
                      value: reportType.label,
                    }))}
                    selected={{
                      id: selectedReportType.value,
                      value: selectedReportType.label,
                    }}
                    handleChange={handleSelectedReportTypeChange}
                    dataCy="dropdown-report-type"
                    disabled={selectedReportConfig.reportId !== ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Report Span
                  </label>
                  <DropdownFull
                    options={timeSpans.map((timeSpan) => ({
                      id: timeSpan.value,
                      value: timeSpan.label,
                    }))}
                    selected={{
                      id: selectedTimeSpans.value,
                      value: selectedTimeSpans.label,
                    }}
                    disabled={disabledSpan}
                    handleChange={handleSelectedTimeSpanChange}
                    dataCy="dropdown-report-span"
                  />
                  {selectedTimeSpans.value === 'SPECIFICDATES' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Date Range
                      </label>
                      <Datepicker
                        key={selectedReportConfig.reportId} // Add a key to force re-render
                        initDates={selectedDates}
                        handleDateChange={handleSelectedDates}
                        mode="range"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Locations
                  </label>
                  {/* Group Filters -- hide if no group options */}
                  {groupOptions.length === 0 ? (
                    <></>
                  ) : (
                    <>
                      <GroupsButton
                        onClick={() => {
                          setIsOpen((prev) => !prev);
                        }}
                      />
                      {/* <div className="flex items-center gap-2 mt-2">
                        <span
                          className={`text-xs ${
                            fetchGroupData ? 'text-slate-500' : 'text-slate-400'
                          }`}
                        >
                          Individuals
                        </span>
                        <div className="relative group">
                          <button
                            id="fetch-group-data-switch"
                            type="button"
                            disabled={cachedGroups.length === 0}
                            aria-pressed={fetchGroupData}
                            onClick={() => {
                              setFetchGroupData((prev) => !prev);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                                                ${
                                                  fetchGroupData
                                                    ? 'bg-blue-600'
                                                    : 'bg-gray-300 dark:bg-gray-600'
                                                }
                                                ${
                                                  cachedGroups.length === 0
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : ''
                                                }
                                              `}
                            data-cy="fetch-group-data-switch"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                                ${
                                                  fetchGroupData
                                                    ? 'translate-x-5'
                                                    : 'translate-x-1'
                                                }
                                                `}
                            />
                          </button>
                          {cachedGroups.length === 0 && (
                            <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-max -translate-x-1/2 rounded bg-slate-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                              Select a group first
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-xs ${
                            fetchGroupData ? 'text-slate-400' : 'text-slate-500'
                          }`}
                        >
                          Group
                        </span>
                      </div> */}
                      <div hidden={true}>
                        <ModalBasic
                          title="Groups"
                          isOpen={isOpen}
                          setIsOpen={setIsOpen}
                          dataCy="modal-filter-groups"
                          full_h={true}
                        >
                          <GroupModal
                            groupOptions={groupOptions}
                            refreshGroups={handleRefreshGroups}
                          />
                        </ModalBasic>
                      </div>
                    </>
                  )}
                  <DropdownLocationMultiple />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Detection Type
                  </label>
                  <DriveThruLobbyToggle />
                </div>
              </div>
            </div>

            {selectedReportType.value === 'PIVOTCOMP' && (
              <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-300 mb-1">
                      SOS Metrics
                    </label>
                    <DropdownFilter

                        title={
      selectedMeasures.length === 1
        ? selectedMeasures[0].label // If length is 1, show the label of the first (and only) item
        : `${selectedMeasures.length} Measures Selected` // Otherwise, show the count message
    }
                      options={naturalSortOptions(
                        pivotMeasures.map((pivotMeasure) => ({
                          id: pivotMeasure.value,
                          value: pivotMeasure.label,
                        }))
                      )}
                      selected={selectedMeasures.map((pivotMeasure) => ({
                        id: pivotMeasure.value,
                        value: pivotMeasure.label,
                      }))}
                      handleChange={handleSelectedMeasuresChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Pivot*
                    </label>
                    <DropdownFilter
                      title={
                        selectedSplitBy.length === 1
                          ? selectedSplitBy[0].label // If length is 1, show the label of the first (and only) item
                          : `${selectedSplitBy.length} Pivots Selected` // Otherwise, show the count message
                      }
                      options={naturalSortOptions(
                        pivotSplitBy.map((splitBy) => ({
                          id: splitBy.value,
                          value: splitBy.label,
                        }))
                      )}
                      selected={selectedSplitBy.map((splitBy) => ({
                        id: splitBy.value,
                        value: splitBy.label,
                      }))}
                      handleChange={handleSelectedSplitByChange}
                    />
                  </div>
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-800 dark:text-slate-300 mb-1">
                          Filter By
                        </label>
                        <DropdownFilter
                          title={
                            selectedFilterCategories.length === 1
                              ? selectedFilterCategories[0].label // If length is 1, show the label of the first (and only) item
                              : `${selectedFilterCategories.length} Filters Selected` // Otherwise, show the count message
                          }
                          options={filterCategories.map((category) => ({
                            // Use the new filterCategories defined above
                            id: category.value,
                            value: category.label,
                          }))}
                          selected={selectedFilterCategories.map(
                            (category) => ({
                              id: category.value,
                              value: category.label,
                            })
                          )}
                          handleChange={handleSelectedFilterCategoriesChange} // New handler
                          dataCy="dropdown-filter-categories"
                        />

                        <div className="bg-white dark:bg-slate-600 rounded-lg shadow p-6 space-y-4">
                          {selectedFilterCategories.map((category) => (
                            <div key={category.value} className="mt-4">
                              {' '}
                              {/* Use key for list rendering */}
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Select {category.label}
                              </label>
                              <DropdownFilter
                                 title={
                                  selectedFilterItems[category.value]?.length === 1
                                    ? selectedFilterItems[category.value][0] // If length is 1, show the label of the first (and only) item
                                    : `${selectedFilterItems[category.value]?.length || 0} Filters Selected` // Otherwise, show the count message
                                }

                                options={
                                  availableFilterOptions[category.value] || []
                                } // Provide fetched options
                                disabled={!selectedSplitBy.some(item => item.value === category.value)}
                                selected={
                                  selectedFilterItems[category.value]?.map(
                                    (id) => {
                                      // Map selected IDs back to {id, value} pairs for the DropdownFilter
                                      const option = availableFilterOptions[
                                        category.value
                                      ]?.find((opt) => opt.id === id);
                                      return { id, value: option?.value || id }; // Use value if found, else id
                                    }
                                  ) || []
                                }
                                handleChange={(itemId) => {
                                  handleSelectedFilterItemsChange(
                                    category.value,
                                    itemId
                                  );
                                }} // New handler
                                dataCy={`dropdown-filter-items-${category.value}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Conditionally render item selectors for each selected filter category */}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {selectedReportType.value === 'CUSTOMERJOURNEY' && (
              <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-300 mb-1">
                      Tile Selection
                    </label>
                    <DropdownFilter

                        title={
                          selectedTiles.length === 1
                            ? selectedTiles[0].label // If length is 1, show the label of the first (and only) item
                            : `${selectedTiles.length} Measures Selected` // Otherwise, show the count message
                          }
                      options={naturalSortOptions(
                        availableTiles.map((tile) => ({
                          id: tile.value,
                          value: tile.label,
                        }))
                      )}
                      selected={selectedTiles.map((tile) => ({
                        id: tile.value,
                        value: tile.label,
                      }))}
                      handleChange={handleSelectedTilesChange}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <button
                className={`btn bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed ${isLoading ? 'animate-pulse' : ''}`}

                disabled={
                  (selectedReportType.value === 'PIVOTCOMP' &&
                    selectedMeasures.length === 0) ||
                  selectedLocations.length === 0 ||
                  selectedReportType.value === '' ||
                  selectedTimeSpans.value === '' ||
                  selectedReportName === '' || 
                  isLoading
                }
                onClick={() => void handleSendReportSelf()}
              >
                Send Test Report to Email
              </button>
              {selectedReportConfig.reportId === '' ? (
                <button
                  className="btn bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={
                    (selectedReportType.value === 'PIVOTCOMP' &&
                      selectedMeasures.length === 0) ||
                    selectedLocations.length === 0 ||
                    selectedReportType.value === '' ||
                    selectedTimeSpans.value === '' ||
                    selectedReportName === '' ||
                    selectedReportConfig.reportId !== ''
                  }
                  onClick={() => void handleSaveReport()}
                >
                  Save Configuration
                </button>
              ) : (
                <button
                  className="btn bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={
                    (selectedReportType.value === 'PIVOTCOMP' &&
                      selectedMeasures.length === 0) ||
                    selectedLocations.length === 0 ||
                    selectedReportType.value === '' ||
                    selectedTimeSpans.value === '' ||
                    selectedReportName === '' ||
                    selectedReportConfig.reportId === ''
                  }
                  onClick={() => void handleSaveReport()}
                >
                  Update Configuration
                </button>
              )}

              <button
                className="btn bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={selectedReportConfig.reportId === ''}
                onClick={() => {
                  void (async () => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this configuration?'
                      )
                    ) {
                      await handleDeleteReport();
                    }
                  })();
                }}
              >
                Delete Configuration
              </button>
            </div>
          </div>
        </div>
    </>
  );
}
